import { useMemo } from "react";

import { useFieldArray, type UseFormReturn } from "react-hook-form";
import { type z } from "zod";

import { useFeatureFlags } from "@hooks/http";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";

import { useShowReferencePeriod } from "../../hooks/use-show-reference-period";
import { type DataColumn, type SpendConstraint } from "../types";

import { AppendSpendConstraintButton } from "./append-spend-constrait-button";
import { type ClientSettingSchema } from "./client-setting-form";
import { SpendConstraintFormComponent } from "./spend-constraint-form-component";

type Props = {
  formMeta: UseFormReturn<z.infer<typeof ClientSettingSchema>>;
  spendConstraintDataColumns: DataColumn[];
  previousPeriodSpendData: {
    totalSpend: number;
    perChannelSpend: Record<string, number>;
  };
  dataColumns: DataColumn[];
  settingPeriodLength: number;
  spendConstraints: SpendConstraint[];
};

export const SettingSpendRequirementsField = ({
  formMeta,
  spendConstraintDataColumns,
  previousPeriodSpendData,
  dataColumns,
  settingPeriodLength,
  spendConstraints,
}: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);

  const enableMmmReferencePeriod = useShowReferencePeriod();
  const {
    fields: spendConstraintFields,
    append: appendSpendConstraintField,
    remove: removeSpendConstraintField,
    update: updateSpendConstraintField,
  } = useFieldArray({
    name: "spend_constraints",
    control: formMeta.control,
  });

  // NOTE: ERRORS DON'T SEEM TO WORK WITH V2 FORM COMPONENTS
  // WHEN USING A VARIABLE ARRAY OF OBJECTS IN THE FORM
  const spendConstraintsErrors = formMeta.formState.errors.spend_constraints;
  // const mixValidationsErrors = formMeta.formState.errors.mix_validations;

  const currentSpendConstraints = formMeta.watch("spend_constraints");

  const spendConstraintErrorMessages =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    formMeta.formState.errors.invalid_spend_constraints?.message;

  const spendConstraintDataColumsById = useMemo(
    () =>
      spendConstraintDataColumns.reduce(
        (acc, e) => ({ ...acc, [e.id]: e }),
        {},
      ),
    [spendConstraintDataColumns],
  );

  const dataColumnsMap = useMemo(() => {
    const map = new Map<string, DataColumn>();
    dataColumns.forEach((dataColumn) => {
      map.set(String(dataColumn.id), dataColumn);
    });
    return map;
  }, [dataColumns]);

  return (
    <div className="mt-0 border-b border-v2-secondary px-6 pt-5 pb-6 bg-v2-secondary">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold">
          {enableMmmReferencePeriod ? "6. " : "5. "}
          Specify any spend requirements (optional)
        </div>
        {spendConstraintErrorMessages && (
          <div className="text-mars text-xs">
            {spendConstraintErrorMessages}
          </div>
        )}
      </div>
      <div className="italic text-xs mb-2">
        E.g: Maximum Branded Search, Minimum Facebook Spend, etc.
      </div>
      <p className="text-sm mb-2">
        By imposing requirements, budget recommendations will be less optimal.
        There are rare cases where budgets
        <br /> cannot be recommended because constraints are too strict - we
        suggest imposing as few
        <br /> constraints as possible.
      </p>
      {spendConstraintFields.length > 0 && (
        <div className="mt-5 mb-2 flex justify-end gap-[44px] mr-[26px] font-bold">
          <span className="uppercase text-xs">Min. entire period</span>
          <span className="uppercase text-xs">Max. entire period</span>
        </div>
      )}
      <div className="flex flex-col gap-4">
        {spendConstraintFields.map((spendConstraintField, index) => {
          const col =
            spendConstraintDataColumsById[spendConstraintField.data_column_id];

          return (
            <SpendConstraintFormComponent
              key={spendConstraintField.id}
              previousPeriodSpend={
                previousPeriodSpendData.perChannelSpend[col?.column_name ?? ""]
              }
              spendConstraintField={spendConstraintField}
              remove={(spendConstraintIndex) => {
                trackMMMEvent({
                  component: ExploreBudgetsTrackedComponent.CLIENT_SETTING_FORM,
                  action: "onClick",
                  actionInfo: "Remove client setting spend constraint",
                });

                removeSpendConstraintField(spendConstraintIndex);
              }}
              update={(i, value) => {
                trackMMMEvent({
                  component: ExploreBudgetsTrackedComponent.CLIENT_SETTING_FORM,
                  action: "onClick",
                  actionInfo: "Update client setting spend constraint",
                });

                updateSpendConstraintField(i, value);
              }}
              form={formMeta}
              dataColumnsMap={dataColumnsMap}
              index={index}
              periodLengthDays={settingPeriodLength}
              minBudgetValue={
                typeof currentSpendConstraints[index].budget_low === "string" &&
                currentSpendConstraints[index].budget_low !== ""
                  ? Number.parseFloat(
                      currentSpendConstraints[index].budget_low as string,
                    )
                  : null
              }
              maxBudgetValue={
                typeof currentSpendConstraints[index].budget_high ===
                  "string" && currentSpendConstraints[index].budget_high !== ""
                  ? Number.parseFloat(
                      currentSpendConstraints[index].budget_high as string,
                    )
                  : null
              }
              hasError={!!spendConstraintsErrors?.[index]}
              existingSpendConstraints={spendConstraints}
            />
          );
        })}
      </div>
      <AppendSpendConstraintButton
        previousPeriodSpendData={previousPeriodSpendData.perChannelSpend}
        append={(value) => {
          trackMMMEvent({
            component: ExploreBudgetsTrackedComponent.CLIENT_SETTING_FORM,
            action: "onClick",
            actionInfo: "Add client setting spend constraint",
          });

          appendSpendConstraintField(value);
        }}
        availableDataColumns={spendConstraintDataColumns}
        selectedDataColumnsIds={currentSpendConstraints.map(
          (c) => c.data_column_id,
        )}
      />
    </div>
  );
};
